import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/NavBar.css';
import { Tooltip, Box, IconButton, Avatar, Typography, Divider, Menu, MenuItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import AppsIcon from '@mui/icons-material/Apps';
import BarChartIcon from '@mui/icons-material/BarChart';
import CableIcon from '@mui/icons-material/Cable';
import SettingsIcon from '@mui/icons-material/Settings';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutButton from './LogoutButton';
import { FEATURE_FLAGS } from '../../featureFlags';
import { useThemeMode } from '../../theme';

import navChat        from '../../assets/logos/nav/nav-chat.svg';
import navCollaborate from '../../assets/logos/nav/nav-collaborate.svg';
import navAgents      from '../../assets/logos/nav/nav-agents.png';
import navAnalytics   from '../../assets/logos/nav/nav-analytics.png';
import navConnectors  from '../../assets/logos/nav/nav-connectors.png';
import navMonitor     from '../../assets/logos/nav/nav-monitor.png';

interface NavTab {
  key: string;
  label: string;
  icon: React.ReactNode;
  index: number;
  enabled: boolean;
}

const navImg = (src: string, label: string, style?: React.CSSProperties) => (
  <img src={src} alt={label} width={32} height={32} style={{ objectFit: 'contain', ...style }} />
);

const TABS_NEW: NavTab[] = [
  { key: 'chat',        label: 'Chat',          icon: navImg(navChat,        'Chat'),          index: 0, enabled: true  },
  { key: 'collaborate', label: 'Collaborate',   icon: navImg(navCollaborate, 'Collaborate'),   index: 1, enabled: true  },
  { key: 'agents',      label: 'Agents & Apps', icon: navImg(navAgents,      'Agents & Apps', { filter: 'brightness(0) invert(1)' }), index: 2, enabled: true },
];

const TABS_LEGACY: NavTab[] = [
  { key: 'chat',        label: 'Chat',          icon: <ChatBubbleOutlineIcon sx={{ fontSize: '1.8rem', color: '#fff' }} />, index: 0, enabled: true  },
  { key: 'collaborate', label: 'Collaborate',   icon: <GroupsIcon           sx={{ fontSize: '1.8rem', color: '#fff' }} />, index: 1, enabled: true  },
  { key: 'agents',      label: 'Agents & Apps', icon: <AppsIcon             sx={{ fontSize: '1.8rem', color: '#fff' }} />, index: 2, enabled: false },
  { key: 'analytics',   label: 'Analytics',     icon: <BarChartIcon         sx={{ fontSize: '1.8rem', color: '#fff' }} />, index: 3, enabled: false },
  { key: 'connectors',  label: 'Connectors',    icon: <CableIcon            sx={{ fontSize: '1.8rem', color: '#fff' }} />, index: 4, enabled: false },
  { key: 'monitor',     label: 'Monitor',       icon: <SettingsIcon         sx={{ fontSize: '1.8rem', color: '#fff' }} />, index: 5, enabled: false },
];

const TABS = FEATURE_FLAGS.newUI ? TABS_NEW : TABS_LEGACY;

interface NavBarProps {
  logedInUserMail: string;
  logedInUserName?: string;
  currentUser?: { name?: string; email?: string } | null;
  changeSelectedNavBar: (index: number) => void;
  selectedNavBar: number;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] ?? '?').toUpperCase();
};

const nameFromEmail = (email: string): string => {
  const local = email.split('@')[0] ?? '';
  return local
    .split('.')
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
};

const NavBar = ({ logedInUserMail, logedInUserName, currentUser, changeSelectedNavBar, selectedNavBar }: NavBarProps) => {
  const navigate = useNavigate();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<HTMLElement | null>(null);
  const { mode, setMode } = useThemeMode();

  const email = currentUser?.email || logedInUserMail || '';
  const displayName = currentUser?.name || logedInUserName || (email ? nameFromEmail(email) : '');
  const initials = displayName ? getInitials(displayName) : '?';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target.closest('.MuiDialog-root') || target.closest('.MuiModal-root')) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="navBarContainer">
      <div className="topNav">
        <div className="navTopSpacer" />
        <div className="iconContainer">
          {TABS.map((tab) => {
            const isSelected = selectedNavBar === tab.index;
            const tooltipTitle = tab.enabled ? tab.label : 'Coming soon';
            return (
              <Tooltip key={tab.key} title={tooltipTitle} placement="right" arrow>
                <div
                  className={`nav-item${isSelected ? ' active' : ''}${!tab.enabled ? ' nav-item--disabled' : ''}`}
                  onClick={() => {
                    if (!tab.enabled) return;
                    if (tab.index === 2) { navigate('/apps'); return; }
                    changeSelectedNavBar(tab.index);
                  }}
                  role={tab.enabled ? 'button' : undefined}
                  aria-disabled={!tab.enabled}
                >
                  <div className="nav-item-pill">
                    <div className="nav-item-icon">{tab.icon}</div>
                    <span className="nav-item-label">{tab.label}</span>
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <div
        className="helpContainer"
        onClick={() => setIsDropdownVisible((prev) => !prev)}
        ref={dropdownRef}
      >
        <Tooltip title="Account" placement="right" arrow>
          <IconButton
            sx={{
              color: 'white',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <AccountCircleIcon sx={{ fontSize: '2.2rem' }} />
          </IconButton>
        </Tooltip>

        {isDropdownVisible && (
          <div className="dropdown-content" onClick={(e) => e.stopPropagation()}>
            {FEATURE_FLAGS.newUI ? (
              <Box sx={{ width: 240 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2.5, pb: 2, px: 2 }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: '#a8c4f0', color: '#1a56c4', fontWeight: 700, fontSize: '1.1rem', mb: 1.25 }}>
                    {initials}
                  </Avatar>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a1f2e', fontFamily: "'Proxima Nova', sans-serif", lineHeight: 1.3 }}>
                    {displayName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: "'Proxima Nova', sans-serif", mt: 0.25 }}>
                    {email}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ py: 0.5 }}>
                  <ListItemButton
                    onClick={(e) => setThemeMenuAnchor(e.currentTarget)}
                    sx={{ px: 2, py: 1, gap: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      <DarkModeOutlinedIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Theme"
                      slotProps={{ primary: { fontSize: '0.9rem', fontFamily: "'Proxima Nova', sans-serif", color: '#1a1f2e' } }}
                    />
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0857c3', fontFamily: "'Proxima Nova', sans-serif", textTransform: 'capitalize', mr: 0.25 }}>
                      {mode}
                    </Typography>
                    <KeyboardArrowDownIcon fontSize="small" sx={{ color: '#0857c3' }} />
                  </ListItemButton>
                  <Menu
                    anchorEl={themeMenuAnchor}
                    open={Boolean(themeMenuAnchor)}
                    onClose={() => setThemeMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{ paper: { sx: { borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 120 } } }}
                  >
                    <MenuItem
                      selected={mode === 'light'}
                      onClick={() => { setMode('light'); setThemeMenuAnchor(null); }}
                      sx={{ fontSize: '0.9rem', fontFamily: "'Proxima Nova', sans-serif" }}
                    >
                      Light
                    </MenuItem>
                    <MenuItem
                      selected={mode === 'dark'}
                      onClick={() => { setMode('dark'); setThemeMenuAnchor(null); }}
                      sx={{ fontSize: '0.9rem', fontFamily: "'Proxima Nova', sans-serif" }}
                    >
                      Dark
                    </MenuItem>
                  </Menu>
                </Box>
                <Divider />
                <Box sx={{ py: 0.5 }}>
                  <LogoutButton variant="text" fullWidth showIcon />
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 1 }}>
                <LogoutButton variant="text" fullWidth showIcon />
              </Box>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NavBar;
